import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  GitBranch, 
  Plus, 
  X,
  Printer,
  Sparkles,
  Loader2
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const FISHBONE_CATEGORIES = [
  { key: "people", label: "People", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", description: "Staff, training, competency, fatigue" },
  { key: "process", label: "Process", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", description: "Procedures, protocols, workflows" },
  { key: "equipment", label: "Equipment", color: "bg-amber-500/20 text-amber-400 border-amber-500/30", description: "Tools, technology, maintenance" },
  { key: "environment", label: "Environment", color: "bg-purple-500/20 text-purple-400 border-purple-500/30", description: "Physical space, layout, safety" },
  { key: "materials", label: "Materials", color: "bg-pink-500/20 text-pink-400 border-pink-500/30", description: "Supplies, medications, resources" },
  { key: "management", label: "Management", color: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30", description: "Policies, leadership, communication" },
];

export const FishboneTemplate = ({ onAIAssist }) => {
  const [problem, setProblem] = useState("");
  const [causes, setCauses] = useState({
    people: [""],
    process: [""],
    equipment: [""],
    environment: [""],
    materials: [""],
    management: [""],
  });
  const [aiLoading, setAiLoading] = useState(false);
  const [rootCause, setRootCause] = useState("");

  const addCause = (category) => {
    setCauses(prev => ({
      ...prev,
      [category]: [...prev[category], ""]
    }));
  };

  const updateCause = (category, index, value) => {
    setCauses(prev => ({
      ...prev,
      [category]: prev[category].map((c, i) => i === index ? value : c)
    }));
  };

  const removeCause = (category, index) => {
    setCauses(prev => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index)
    }));
  };

  const getAISuggestions = async () => {
    if (!problem.trim()) {
      toast.error("Please enter the problem statement first");
      return;
    }

    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/fishbone`, {
        problem: problem.trim(),
        causes: causes
      }, { withCredentials: true });

      if (onAIAssist) {
        onAIAssist(response.data.draft);
      }
      toast.success("AI analysis complete - check the chat");
    } catch (error) {
      toast.error("Failed to get AI suggestions");
    } finally {
      setAiLoading(false);
    }
  };

  const handlePrint = () => window.print();

  return (
    <Card className="bento-card print:shadow-none print:border-gray-300">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-amber-400" />
            Ishikawa / Fishbone Diagram
          </CardTitle>
          <div className="flex gap-2 no-print">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
            <Button size="sm" onClick={getAISuggestions} disabled={aiLoading}>
              {aiLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              AI Analyze
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Problem Statement - The Fish Head */}
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <Label className="text-red-400 font-medium">Problem Statement (Effect)</Label>
          <Textarea
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
            placeholder="What is the problem you're investigating?"
            className="mt-2 bg-transparent border-red-500/30"
            rows={2}
            data-testid="fishbone-problem"
          />
        </div>

        {/* Causes Grid - The Fish Bones */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FISHBONE_CATEGORIES.map((cat) => (
            <div key={cat.key} className={`p-3 rounded-lg border ${cat.color}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="font-medium">{cat.label}</span>
                  <p className="text-xs opacity-70">{cat.description}</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-6 w-6"
                  onClick={() => addCause(cat.key)}
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
              <div className="space-y-2">
                {causes[cat.key].map((cause, idx) => (
                  <div key={idx} className="flex gap-1">
                    <Input
                      value={cause}
                      onChange={(e) => updateCause(cat.key, idx, e.target.value)}
                      placeholder={`${cat.label} cause...`}
                      className="h-8 text-xs bg-transparent"
                      data-testid={`fishbone-${cat.key}-${idx}`}
                    />
                    {causes[cat.key].length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 flex-shrink-0"
                        onClick={() => removeCause(cat.key, idx)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Root Cause Conclusion */}
        <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
          <Label className="text-emerald-400 font-medium">Identified Root Cause</Label>
          <Textarea
            value={rootCause}
            onChange={(e) => setRootCause(e.target.value)}
            placeholder="Based on analysis, the primary root cause is..."
            className="mt-2 bg-transparent border-emerald-500/30"
            rows={2}
            data-testid="fishbone-root-cause"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default FishboneTemplate;
